import React, { useMemo } from "react";
import isEmpty from "lodash/isEmpty";
import useWindowDimensions from "hooks/useWindowDimensions";
import { formatLowCount } from "utils/formatter";
import EyeIcon from "images/eye-icon.svg";
import PlayButtonIcon from "images/play-button-icon.svg";
import LocationDetails from "./LocationDetails";
import MobileFullWidthReviewCard from "./MobileFullWidthReviewCard";
import styles from "./ReviewCard.module.css";

const MOBILE_DEFAULT_POSTER_WIDTH = 120;
const MOBILE_DEFAULT_POSTER_HEIGHT = 184;

const DEFAULT_POSTER_WIDTH = 282;
const DEFAULT_POSTER_HEIGHT = 400;
const ASPECT_RATIO = DEFAULT_POSTER_WIDTH / DEFAULT_POSTER_HEIGHT;

/**
 * At the time of this component's creation 03-14-2024, we do not need the use case
 * for playing the video. We only need to display the poster image.
 * We can certainly extend this component to include the video player when needed.
 * */
const ReviewCard = ({
  fullWidth = false,
  onAuthorClick,
  onClick,
  review,
  showAddress = false,
  showAuthor = false,
  showReadCount = true,
  showTags = false,
  mobileSize = {
    width: MOBILE_DEFAULT_POSTER_WIDTH,
    height: MOBILE_DEFAULT_POSTER_HEIGHT
  },
  size = {
    minWidth: DEFAULT_POSTER_WIDTH,
    minHeight: DEFAULT_POSTER_HEIGHT
  }
}) => {
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const carouselWidth = width * 0.8;
  const carouselPadding = isMobile ? 10 : 24;
  const cardWidth = carouselWidth / 4 - carouselPadding;
  const cardMetaOptions = [showAddress, showAuthor, showTags].filter(Boolean).length;
  const cardMetaHeight = 72 + cardMetaOptions * 32;

  const { video, location } = review || {};

  const readCountLabel = useMemo(() =>
    formatLowCount(video?.clm_read_count)
  , [video?.clm_read_count]);

  if (fullWidth && isMobile) {
    return (
      <MobileFullWidthReviewCard
        review={review}
        onAuthorClick={onAuthorClick}
        onClick={onClick}
        mobileSize={mobileSize}
        size={size}
        showReadCount={showReadCount}
        showAddress={showAddress}
        showAuthor={showAuthor}
        showTags={showTags}
      />
    )
  }

  return (
    <div
      className={styles.reviewCardContainer}
      style={isMobile ? mobileSize : {
        ...size,
        minHeight: size.minHeight + cardMetaHeight,
        width: cardWidth,
        height: cardWidth / ASPECT_RATIO + cardMetaHeight
      }}
      onClick={onClick}
    >
      <img
        alt={location?.clm_name}
        src={video?.urls?.poster}
        className={styles.reviewCardPoster}
        style={isMobile ? mobileSize : {
          ...size,
          width: cardWidth,
          height: cardWidth / ASPECT_RATIO
        }}
      />
      {showReadCount && readCountLabel && (
        <div className={styles.viewCountContainer}>
          <img src={EyeIcon} alt="View count" style={{ width: 16, height: 16 }}/>
          <span className={styles.overlayLabel}>{readCountLabel}</span>
        </div>
      )}
      {isMobile ? (
        <p className={`${styles.overlayLabel} ${styles.locationNameContainer}`}>
          {location?.clm_name}
        </p>
      ) : (
        <img
          src={PlayButtonIcon}
          alt="Play button"
          className={styles.overlayPlayIcon}
          style={{
            width: 40,
            height: 40,
            top: Math.max(cardWidth / ASPECT_RATIO, size.minHeight) / 2
          }}
        />
      )}
      {!isMobile && !isEmpty(location) && (
        <LocationDetails
          location={location}
          showAddress={showAddress}
          showAuthor={showAuthor}
          showTags={showTags}
          author={review?.author}
          onAuthorClick={onAuthorClick}
        />
      )}
    </div>
  )
};

export default ReviewCard;